import React, { useContext } from 'react';
import {
    Text,
    IconButton,
    TooltipHost,
    Icon,
    TextField,
    Label,
    ChoiceGroup,
    IChoiceGroupOption,
    Toggle,
    DefaultButton,
    IContextualMenuItem,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { FlagOverride, EnvironmentFlags } from '../models/EnvironmentModels';
import { Ellipsis } from '../../HubLayout/components/Utilities/Ellipsis';
import { OverrideExtendedFields } from './OverrideExtendedFields';

interface FeatureFlagOverrideCardProps {
    override: FlagOverride;
    index: number;
    isChanged: boolean;
    isCollapsed: boolean;
    isActive: boolean;
    filterType: 'serviceTree' | 'buildPipeline';
    onToggleCollapse: () => void;
    onFilterTypeChange: (newType: 'serviceTree' | 'buildPipeline') => void;
    onFieldChange: <K extends keyof FlagOverride>(
        field: K,
        value: FlagOverride[K]
    ) => void;
    onFeatureFlagChange: (
        flagKey: keyof EnvironmentFlags,
        value: boolean | null
    ) => void;
    onCopyOverride: () => void;
    onCopyToClipboard: () => void;
    onRemove: () => void;
}

export const FeatureFlagOverrideCard: React.FC<FeatureFlagOverrideCardProps> =
    ({
        override,
        index,
        isChanged,
        isCollapsed,
        isActive,
        filterType,
        onToggleCollapse,
        onFilterTypeChange,
        onFieldChange,
        onFeatureFlagChange,
        onCopyOverride,
        onCopyToClipboard,
        onRemove,
    }) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);

        const placeholderStyles = {
            field: {
                selectors: {
                    '::placeholder': {
                        color: theme.palette.neutralQuaternary,
                        opacity: 0.7,
                        fontStyle: 'italic',
                    },
                },
            },
        };

        const menuItems: IContextualMenuItem[] = [
            {
                key: 'copy',
                text: 'Copy Override (JSON)',
                iconProps: { iconName: 'Copy' },
                onClick: onCopyOverride,
            },
            {
                key: 'copyToClipboard',
                text: 'Copy to Clipboard',
                iconProps: { iconName: 'ClipboardList' },
                onClick: onCopyToClipboard,
            },
            {
                key: 'remove',
                text: 'Remove',
                iconProps: { iconName: 'Delete' },
                onClick: onRemove,
            },
        ];

        const filterTypeOptions: IChoiceGroupOption[] = [
            {
                key: 'serviceTree',
                text: 'Service Tree ID',
                iconProps: { iconName: 'Org' },
            },
            {
                key: 'buildPipeline',
                text: 'Build Pipeline',
                iconProps: { iconName: 'Build' },
            },
        ];

        const hasServiceTree =
            override.serviceTreeId != null &&
            override.serviceTreeId.trim() !== '';
        const hasBuildPipeline =
            (override.accountName != null &&
                override.accountName.trim() !== '') ||
            (override.projectName != null &&
                override.projectName.trim() !== '') ||
            (override.branchName != null &&
                override.branchName.trim() !== '') ||
            (override.releaseDefinitionId != null &&
                override.releaseDefinitionId.trim() !== '');
        const hasBothFilterTypes = hasServiceTree && hasBuildPipeline;

        const renderFeatureFlags = () => {
            const flagEntries = Object.entries(override.featureFlags || {}) as [
                keyof EnvironmentFlags,
                boolean | null | undefined
            ][];

            return (
                <>
                    <Label
                        styles={{
                            root: { fontWeight: 600, marginBottom: '8px' },
                        }}
                    >
                        Feature Flags
                    </Label>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '12px',
                            padding: '12px',
                            backgroundColor: theme.palette.neutralLighterAlt,
                        }}
                    >
                        {flagEntries.map(([key, value]) => {
                            const displayName = key
                                .replace(/([A-Z])/g, ' $1')
                                .trim();
                            const capitalizedName =
                                displayName.charAt(0).toUpperCase() +
                                displayName.slice(1);

                            return (
                                <div
                                    key={key}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        padding: '10px',
                                        backgroundColor: theme.palette.white,
                                        border: `1px solid ${theme.palette.neutralLight}`,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Label>{capitalizedName}</Label>
                                    <div
                                        style={{ display: 'flex', gap: '8px' }}
                                    >
                                        <DefaultButton
                                            text="True"
                                            onClick={() =>
                                                onFeatureFlagChange(key, true)
                                            }
                                            styles={{
                                                root: {
                                                    minWidth: '60px',
                                                    backgroundColor:
                                                        value === true
                                                            ? theme.palette
                                                                  .green
                                                            : undefined,
                                                    color:
                                                        value === true
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootHovered: {
                                                    backgroundColor:
                                                        value === true
                                                            ? theme.palette
                                                                  .green
                                                            : undefined,
                                                    color:
                                                        value === true
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootPressed: {
                                                    backgroundColor:
                                                        value === true
                                                            ? theme.palette
                                                                  .green
                                                            : undefined,
                                                    color:
                                                        value === true
                                                            ? 'white'
                                                            : undefined,
                                                },
                                            }}
                                        />
                                        <DefaultButton
                                            text="False"
                                            onClick={() =>
                                                onFeatureFlagChange(key, false)
                                            }
                                            styles={{
                                                root: {
                                                    minWidth: '60px',
                                                    backgroundColor:
                                                        value === false
                                                            ? theme.palette
                                                                  .redDark
                                                            : undefined,
                                                    color:
                                                        value === false
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootHovered: {
                                                    backgroundColor:
                                                        value === false
                                                            ? theme.palette
                                                                  .redDark
                                                            : undefined,
                                                    color:
                                                        value === false
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootPressed: {
                                                    backgroundColor:
                                                        value === false
                                                            ? theme.palette
                                                                  .redDark
                                                            : undefined,
                                                    color:
                                                        value === false
                                                            ? 'white'
                                                            : undefined,
                                                },
                                            }}
                                        />
                                        <DefaultButton
                                            text="Not Set"
                                            onClick={() =>
                                                onFeatureFlagChange(key, null)
                                            }
                                            styles={{
                                                root: {
                                                    minWidth: '60px',
                                                    backgroundColor:
                                                        value === null
                                                            ? theme.palette
                                                                  .neutralSecondary
                                                            : undefined,
                                                    color:
                                                        value === null
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootHovered: {
                                                    backgroundColor:
                                                        value === null
                                                            ? theme.palette
                                                                  .neutralSecondary
                                                            : undefined,
                                                    color:
                                                        value === null
                                                            ? 'white'
                                                            : undefined,
                                                },
                                                rootPressed: {
                                                    backgroundColor:
                                                        value === null
                                                            ? theme.palette
                                                                  .neutralSecondary
                                                            : undefined,
                                                    color:
                                                        value === null
                                                            ? 'white'
                                                            : undefined,
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            );
        };

        // Summarize active flags for collapsed view
        const getActiveFlags = (): string => {
            const flags = override.featureFlags || {};
            const activeFlags: string[] = [];
            (
                Object.entries(flags) as [string, boolean | null | undefined][]
            ).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    const displayName = key.replace(/([A-Z])/g, ' $1').trim();
                    activeFlags.push(`${displayName}: ${value}`);
                }
            });
            return activeFlags.length > 0
                ? activeFlags.join(', ')
                : 'No flags set';
        };

        return (
            <div
                style={{
                    padding: '15px',
                    backgroundColor: theme.palette.neutralLighterAlt,
                    borderTop: isChanged
                        ? `2px solid ${theme.palette.themePrimary}`
                        : `1px solid ${theme.palette.neutralLight}`,
                    borderRight: isChanged
                        ? `2px solid ${theme.palette.themePrimary}`
                        : `1px solid ${theme.palette.neutralLight}`,
                    borderBottom: isChanged
                        ? `2px solid ${theme.palette.themePrimary}`
                        : `1px solid ${theme.palette.neutralLight}`,
                    borderLeft: isActive
                        ? `4px solid ${theme.palette.green}`
                        : `4px solid ${theme.palette.neutralTertiary}`,
                    borderRadius: '4px',
                    boxShadow:
                        '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                }}
            >
                {/* Header with name, collapse button, and actions */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: isCollapsed ? '0' : '16px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 1,
                        }}
                    >
                        <IconButton
                            iconProps={{
                                iconName: isCollapsed
                                    ? 'ChevronRight'
                                    : 'ChevronDown',
                            }}
                            title={isCollapsed ? 'Expand' : 'Collapse'}
                            ariaLabel={`${
                                isCollapsed ? 'Expand' : 'Collapse'
                            } feature flag override ${
                                override.name || index + 1
                            }`}
                            aria-expanded={!isCollapsed}
                            onClick={onToggleCollapse}
                            styles={{ root: { height: 32, width: 32 } }}
                        />
                        <div style={{ flex: 1, marginRight: '12px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <Text
                                    variant="large"
                                    styles={{ root: { fontWeight: 600 } }}
                                >
                                    {override.name || `Override ${index + 1}`}
                                </Text>
                                <TooltipHost
                                    content={
                                        isActive
                                            ? 'This override is currently active'
                                            : 'This override is not active'
                                    }
                                >
                                    <Icon
                                        iconName={
                                            isActive
                                                ? 'StatusCircleCheckmark'
                                                : 'StatusCircleRing'
                                        }
                                        styles={{
                                            root: {
                                                color: isActive
                                                    ? theme.palette.green
                                                    : theme.palette
                                                          .neutralSecondary,
                                                fontSize: 16,
                                            },
                                        }}
                                    />
                                </TooltipHost>
                            </div>
                            {isCollapsed && (
                                <>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                                display: 'block',
                                                marginTop: '4px',
                                            },
                                        }}
                                    >
                                        {filterType === 'serviceTree' &&
                                            override.serviceTreeId &&
                                            `Service Tree: ${override.serviceTreeId}`}
                                        {filterType === 'buildPipeline' &&
                                            (override.accountName ||
                                                override.projectName) &&
                                            `${override.accountName || ''}${
                                                override.accountName &&
                                                override.projectName
                                                    ? '/'
                                                    : ''
                                            }${override.projectName || ''}`}
                                        {` | Flags: ${getActiveFlags()}`}
                                    </Text>
                                    {override.tags && override.tags.length > 0 && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '4px',
                                                marginTop: '6px',
                                            }}
                                        >
                                            {override.tags.map(
                                                (tag, tagIndex) => (
                                                    <span
                                                        key={tagIndex}
                                                        style={{
                                                            padding: '2px 8px',
                                                            backgroundColor:
                                                                theme.palette
                                                                    .themeLighter,
                                                            border: `1px solid ${theme.palette.themePrimary}`,
                                                            borderRadius:
                                                                '10px',
                                                            fontSize: '11px',
                                                            color: theme.palette
                                                                .themeDarker,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {tag}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {isChanged && (
                            <TooltipHost content="This override has unsaved changes">
                                <Icon
                                    iconName="EditNote"
                                    styles={{
                                        root: {
                                            color: theme.palette.themePrimary,
                                            fontSize: 16,
                                        },
                                    }}
                                />
                            </TooltipHost>
                        )}
                        <Ellipsis menuItems={menuItems} />
                    </div>
                </div>

                {/* Collapsible Content */}
                {!isCollapsed && (
                    <>
                        {/* Name Field */}
                        <div style={{ marginBottom: '16px' }}>
                            <TextField
                                label="Override Name"
                                value={override.name || ''}
                                onChange={(_, newValue) =>
                                    onFieldChange('name', newValue || '')
                                }
                                placeholder="Enter a descriptive name"
                                required
                                styles={placeholderStyles}
                            />
                        </div>

                        {/* Filter Type Selection */}
                        <div
                            style={{
                                marginBottom: '16px',
                                padding: '12px',
                                backgroundColor: theme.palette.white,
                                borderRadius: '4px',
                            }}
                        >
                            <Label
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        marginBottom: '8px',
                                    },
                                }}
                            >
                                Filter Type
                            </Label>
                            <ChoiceGroup
                                selectedKey={filterType}
                                options={filterTypeOptions}
                                onChange={(_, option) => {
                                    if (option) {
                                        onFilterTypeChange(
                                            option.key as
                                                | 'serviceTree'
                                                | 'buildPipeline'
                                        );
                                    }
                                }}
                                styles={{
                                    flexContainer: {
                                        display: 'flex',
                                        gap: '20px',
                                    },
                                }}
                            />
                            <Text
                                variant="small"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                        marginTop: '8px',
                                        display: 'block',
                                    },
                                }}
                            >
                                {filterType === 'serviceTree'
                                    ? 'Filter by Service Tree ID - matches any deployment from this service'
                                    : 'Filter by Build Pipeline - matches specific account, project, branch, or release definition'}
                            </Text>
                            {hasBothFilterTypes && (
                                <MessageBar
                                    messageBarType={MessageBarType.warning}
                                    isMultiline
                                    styles={{
                                        root: {
                                            marginTop: '8px',
                                            backgroundColor: '#4d3a00',
                                        },
                                        text: { color: '#ffffff' },
                                        icon: { color: '#ffb900' },
                                        innerText: { color: '#ffffff' },
                                    }}
                                >
                                    Both Service Tree ID and Build Pipeline
                                    fields are populated. SRX prioritizes
                                    Service Tree ID — when set, the build
                                    pipeline fields (Account, Project, Branch,
                                    Release Definition) are ignored. Clear one
                                    set of fields to avoid confusion.
                                </MessageBar>
                            )}
                        </div>

                        {/* Conditional Filter Fields */}
                        <div style={{ marginBottom: '16px' }}>
                            <Label
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        marginBottom: '8px',
                                    },
                                }}
                            >
                                Filter Criteria
                            </Label>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '12px',
                                    padding: '12px',
                                    backgroundColor: theme.palette.white,
                                    borderRadius: '4px',
                                }}
                            >
                                {filterType === 'serviceTree' ? (
                                    <TextField
                                        label="Service Tree ID"
                                        value={override.serviceTreeId || ''}
                                        onChange={(_, newValue) =>
                                            onFieldChange(
                                                'serviceTreeId',
                                                newValue || null
                                            )
                                        }
                                        placeholder="Enter GUID"
                                        required
                                        description="The Service Tree ID (GUID) to match"
                                        styles={placeholderStyles}
                                    />
                                ) : (
                                    <>
                                        <TextField
                                            label="Account Name"
                                            value={override.accountName || ''}
                                            onChange={(_, newValue) =>
                                                onFieldChange(
                                                    'accountName',
                                                    newValue || null
                                                )
                                            }
                                            placeholder="e.g., msazuredev"
                                            description="Azure DevOps account name"
                                            styles={placeholderStyles}
                                        />

                                        <TextField
                                            label="Project Name"
                                            value={override.projectName || ''}
                                            onChange={(_, newValue) =>
                                                onFieldChange(
                                                    'projectName',
                                                    newValue || null
                                                )
                                            }
                                            placeholder="e.g., AzureBridge"
                                            description="Azure DevOps project name"
                                            styles={placeholderStyles}
                                        />

                                        <TextField
                                            label="Branch Name"
                                            value={override.branchName || ''}
                                            onChange={(_, newValue) =>
                                                onFieldChange(
                                                    'branchName',
                                                    newValue || null
                                                )
                                            }
                                            placeholder="e.g., main or dcmt.*"
                                            description="Branch name (supports regex)"
                                            styles={placeholderStyles}
                                        />

                                        <TextField
                                            label="Release Definition ID"
                                            value={
                                                override.releaseDefinitionId ||
                                                ''
                                            }
                                            onChange={(_, newValue) =>
                                                onFieldChange(
                                                    'releaseDefinitionId',
                                                    newValue || null
                                                )
                                            }
                                            placeholder="e.g., 16030"
                                            description="Build/Release definition ID"
                                            styles={placeholderStyles}
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Override Configuration: Enabled toggle + Feature Flags */}
                        <div style={{ marginBottom: '12px' }}>
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: theme.palette.white,
                                    borderRadius: '4px',
                                    marginBottom: '12px',
                                }}
                            >
                                <Toggle
                                    label="Enabled"
                                    checked={override.enabled || false}
                                    onChange={(_, checked) =>
                                        onFieldChange(
                                            'enabled',
                                            checked || false
                                        )
                                    }
                                />
                            </div>

                            {renderFeatureFlags()}
                        </div>

                        {/* Extended Fields: Pipeline Source Type, Tags, and Timeframe */}
                        <OverrideExtendedFields
                            pipelineSourceType={override.pipelineSourceType}
                            tags={override.tags || []}
                            startTime={override.startTime}
                            endTime={override.endTime}
                            onPipelineSourceTypeChange={(value) =>
                                onFieldChange('pipelineSourceType', value)
                            }
                            onTagsChange={(tags) => onFieldChange('tags', tags)}
                            onStartTimeChange={(date) =>
                                onFieldChange('startTime', date)
                            }
                            onEndTimeChange={(date) =>
                                onFieldChange('endTime', date)
                            }
                        />
                    </>
                )}
            </div>
        );
    };
